import {useMutation, useQueryClient} from "@tanstack/react-query";
import {message} from "antd";
import {useClientV2} from "../../../../utils/hooks";
import {
  GET_STORE_SUBSCRIPTION_RQ_KEY,
  GET_STORE_SUBSCRIPTION_SUBSCRIBERS_RQ_KEY,
  GET_SUBSCRIPTION_SALES_INSIGHTS_RQ_KEY,
} from "../constants";
import {invalidateSubscriptionListQueries} from "../invalidate-subscription-lists";

export type CancelSubscriptionSubscriberPayload = {
  /**
   * Path `customerSubscriptionId`:
   * `PATCH /subscriptions/subscribers/{customerSubscriptionId}/cancel`.
   */
  customerSubscriptionId: number;
  /** Parent subscription id (for cache invalidation only; not sent in the cancel request). */
  subscriptionId: number;
};

export type CancelSubscriptionSubscriberApiResponse = {
  status: boolean;
  message: string;
  statusCode?: number;
  data?: unknown;
};

function stringifyErrorMessage(value: unknown): string | undefined {
  if (typeof value === "string") {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map(stringifyErrorMessage).filter(Boolean).join(", ");
  }

  if (value && typeof value === "object") {
    const record = value as Record<string, unknown>;
    return (
      stringifyErrorMessage(record.message) ??
      stringifyErrorMessage(record.error) ??
      stringifyErrorMessage(record.errors) ??
      JSON.stringify(record)
    );
  }

  return undefined;
}

function formatError(error: unknown) {
  return stringifyErrorMessage(error) ?? "Could not cancel this subscriber.";
}

export const useCancelSubscriptionSubscriberApi = () => {
  const queryClient = useQueryClient();
  const client = useClientV2();

  return useMutation({
    mutationFn: async ({
      customerSubscriptionId,
    }: CancelSubscriptionSubscriberPayload): Promise<CancelSubscriptionSubscriberApiResponse> => {
      return client(`subscriptions/subscribers/${customerSubscriptionId}/cancel`, {
        method: "PATCH",
      }) as Promise<CancelSubscriptionSubscriberApiResponse>;
    },
    onError: (error: unknown) => {
      message.error(formatError(error));
    },
    onSuccess: (res) => {
      const msg = res?.message?.trim();
      if (msg && msg !== "OK") {
        message.success(msg);
      } else {
        message.success("Subscriber cancelled.");
      }
    },
    onSettled: (_, __, {subscriptionId}) => {
      queryClient.invalidateQueries({
        queryKey: [GET_STORE_SUBSCRIPTION_RQ_KEY, String(subscriptionId)],
      });
      queryClient.invalidateQueries({
        queryKey: [GET_STORE_SUBSCRIPTION_SUBSCRIBERS_RQ_KEY, String(subscriptionId)],
      });
      queryClient.invalidateQueries({
        queryKey: [GET_SUBSCRIPTION_SALES_INSIGHTS_RQ_KEY, String(subscriptionId)],
      });
      invalidateSubscriptionListQueries(queryClient);
    },
  });
};
